import classNames from 'classnames';
import React, { type PropsWithChildren, type ReactElement } from 'react';
import { Button as BsButton, type ButtonProps as BsButtonProps, Spinner } from 'react-bootstrap';

import TooltipOverlay from './TooltipOverlay';

export type ButtonVariant =
  | ''
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'dark'
  | 'light'
  | 'link'
  | 'circle'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-success'
  | 'outline-danger'
  | 'outline-warning'
  | 'outline-info'
  | 'outline-dark'
  | 'outline-light'
  | 'phoenix-primary'
  | 'phoenix-secondary'
  | 'phoenix-success'
  | 'phoenix-danger'
  | 'phoenix-warning'
  | 'phoenix-info'
  | 'phoenix-dark'
  | 'phoenix-light'
  | 'soft-primary'
  | 'soft-secondary'
  | 'soft-success'
  | 'soft-danger'
  | 'soft-warning'
  | 'soft-info'
  | 'soft-dark'
  | 'soft-light'
  | 'loading';

export interface ButtonProps extends BsButtonProps {
  variant?: ButtonVariant;
  startIcon?: ReactElement<React.SVGProps<SVGSVGElement>>; // or any specific icon props type
  endIcon?: ReactElement<React.SVGProps<SVGSVGElement>>;
  loading?: boolean;
  loadingPosition?: 'start' | 'end';
  className?: string;
}

const Button = ({
  children,
  startIcon,
  endIcon,
  loading,
  loadingPosition,
  className,
  variant = '',
  title,
  ...rest
}: PropsWithChildren<ButtonProps>) => {
  const buttonElement = (
    <BsButton
      variant={variant}
      type="button"
      disabled={loading}
      {...rest}
      className={classNames(className, {
        'btn-loading lh-1 d-flex align-items-center position-relative': loading,
      })}
    >
      {loading && loadingPosition === 'start' && (
        <Spinner animation="border" role="status" className="me-2">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      )}

      {startIcon &&
        React.cloneElement(startIcon, {
          className: classNames('me-1', startIcon.props.className),
        })}

      {children}

      {endIcon &&
        React.cloneElement(endIcon, {
          className: classNames('ms-1', endIcon.props.className),
        })}

      {loading && loadingPosition === 'end' && (
        <Spinner animation="border" role="status" className="ms-2">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      )}
    </BsButton>
  );
  if (!title) return buttonElement;

  return (
    <TooltipOverlay title={title} placement="bottom">
      <span className="d-inline-block">{buttonElement}</span>
    </TooltipOverlay>
  );
};

export default Button;
